import { History, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChatCreditsPill } from './ChatCreditsPill';

interface ChatGlassHeaderProps {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  onOpenHistory?: () => void;
  onNewConversation?: () => void;
  onClose?: () => void;
  className?: string;
}

export function ChatGlassHeader({
  title,
  subtitle,
  onBack,
  onOpenHistory,
  onNewConversation: _onNewConversation,
  onClose,
  className,
}: ChatGlassHeaderProps) {
  const { t } = useTranslation('dashboard');

  return (
    <header
      className={cn(
        'bg-[#f5f7f9]/95 dark:bg-charcoal-900/95 backdrop-blur-md border-0 shrink-0 py-1.5 z-20',
        className,
      )}
    >
      {/* Safe-area spacer — only takes space in PWA standalone mode */}
      <div className="[height:env(safe-area-inset-top,0px)]" />
      <div className="flex h-14 items-center justify-between px-3 gap-2">
        {/* Left: History button + Avatar + Title */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {onOpenHistory || onBack ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onOpenHistory ?? onBack}
              aria-label={t('chat_history', { defaultValue: 'Historique' })}
              className="size-8 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-charcoal-800 shrink-0"
            >
              <History className="size-4.5" />
            </Button>
          ) : null}

          {/* Avatar & Name */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="size-9 rounded-full bg-slate-200 overflow-hidden shrink-0 border border-slate-200 dark:border-charcoal-700 shadow-2xs flex items-center justify-center">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80"
                alt="Velko Avatar"
                className="size-full object-cover"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-heading text-base font-bold text-slate-900 dark:text-white leading-tight truncate">
                {title ?? 'Copilote Velko'}
              </span>
              {subtitle && (
                <span className="text-[9px] font-mono font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 truncate hidden sm:block">
                  {subtitle}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Credits (hidden on narrow screens, visible sm+) & Close button */}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="hidden sm:block">
            <ChatCreditsPill />
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label={t('chat_close', { defaultValue: 'Fermer' })}
              className="size-8 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-charcoal-800 shrink-0"
            >
              <X className="size-4.5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
